import { UserAvatar } from '@/components/user-avatar'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { memo, useMemo } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import type { ColumnDef } from '@/libs/table'
import { actionsColumnBase, stickyNameClass, stickyNameCellClass } from '@/components/table-styles'
import type { ColumnMeta } from '@/components/table-styles'
import type { Department, MemberStatus, TeamMember } from '../domain/member'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { session$ } from '@/features/auth/store'
import { openSheet } from '@/features/ui/store'

const statusVariant: Record<MemberStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
	active: 'default',
	on_leave: 'secondary',
	departed: 'outline',
	archived: 'outline',
}

const NameCell = memo(({ name, avatarUrl }: { name: string; avatarUrl: string | null }) => (
	<div className='flex items-center space-x-2'>
		<UserAvatar name={name} avatarUrl={avatarUrl} size='xs' />
		<span className='truncate'>{name}</span>
	</div>
))

const StatusCell = memo(({ status }: { status: MemberStatus }) => {
	const content = useIntlayer('hr-page')
	const label = content.status[status]?.value ?? status
	return <Badge variant={statusVariant[status]}>{label}</Badge>
})

const EmploymentTypeCell = memo(({ type }: { type: string }) => {
	const content = useIntlayer('hr-page')
	return (
		<span className='text-muted-foreground text-sm'>
			{content.employmentType[type as keyof typeof content.employmentType]?.value ?? type}
		</span>
	)
})

const StartDateCell = memo(({ startDate }: { startDate: string | null }) => {
	const { formatPreferenceDate } = useDateFormat()
	return <span>{startDate ? formatPreferenceDate(startDate) : '-'}</span>
})

const ActionsCell = memo(
	({
		memberId,
		memberEmail,
		onArchive,
	}: {
		memberId: string
		memberEmail: string
		onArchive?: (id: string) => void
	}) => {
		const content = useIntlayer('hr-page')
		const user = useSelector(() => session$.user.get())
		const canManage = usePermission('hr.members.manage')
		const canEditOwn = usePermission('hr.members.edit-own')
		const canEdit = canManage || (canEditOwn && user?.email === memberEmail)

		const handleView = () => openSheet('member-details', memberId)
		const handleEdit = () => openSheet('member-edit', memberId)

		return (
			<div className='flex items-center justify-center w-full'>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' className='h-8 w-8 p-0'>
							<Icons.MoreHoriz size={16} />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem onClick={handleView}>{content.actions.view}</DropdownMenuItem>
						{canEdit && <DropdownMenuItem onClick={handleEdit}>{content.actions.edit}</DropdownMenuItem>}
						{canManage && (
							<DropdownMenuItem onClick={() => onArchive?.(memberId)} className='text-FF3638'>
								{content.actions.archive}
							</DropdownMenuItem>
						)}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		)
	},
)

const useMemberColumns = (departments: Department[]) => {
	const content = useIntlayer('hr-page')
	const c = content.columns

	return useMemo<ColumnDef<TeamMember, unknown>[]>(
		() => [
			{
				id: 'name',
				accessorKey: 'fullName',
				header: c.name.value,
				size: 260,
				minSize: 200,
				enableResizing: true,
				meta: {
					headerLabel: c.name.value,
					sticky: true,
					headerClassName: `w-[260px] min-w-[200px] md:sticky md:left-0 bg-background z-10 ${stickyNameClass}`,
					className: `w-[260px] min-w-[200px] md:sticky md:left-0 bg-background group-hover:bg-hover-group  z-10 ${stickyNameCellClass}`,
				} satisfies ColumnMeta,
				cell: ({ row }) => <NameCell name={row.original.fullName} avatarUrl={row.original.avatarUrl} />,
			},
			{
				id: 'position',
				accessorKey: 'position',
				header: c.position.value,
				size: 260,
				minSize: 200,
				enableResizing: true,
				meta: { headerLabel: c.position.value } satisfies ColumnMeta,
				cell: ({ row }) => <span>{row.original.position ?? '-'}</span>,
			},
			{
				id: 'department',
				accessorKey: 'departmentId',
				header: c.department.value,
				size: 360,
				minSize: 200,
				enableResizing: true,
				meta: { headerLabel: c.department.value } satisfies ColumnMeta,
				cell: ({ row }) => {
					const name = departments.find(d => d.id === row.original.departmentId)?.name
					return <span>{name ?? '-'}</span>
				},
			},
			{
				id: 'status',
				accessorKey: 'status',
				header: c.status.value,
				size: 160,
				minSize: 110,
				enableResizing: true,
				meta: { headerLabel: c.status.value } satisfies ColumnMeta,
				cell: ({ row }) => <StatusCell status={row.original.status} />,
			},
			{
				id: 'employmentType',
				accessorKey: 'employmentType',
				header: c.employmentType.value,
				enableResizing: true,
				size: 160,
				minSize: 110,
				meta: { headerLabel: c.employmentType.value } satisfies ColumnMeta,
				cell: ({ row }) => <EmploymentTypeCell type={row.original.employmentType} />,
			},
			{
				id: 'startDate',
				accessorKey: 'startDate',
				header: c.startDate.value,
				size: 100,
				minSize: 80,
				enableResizing: true,
				meta: { headerLabel: c.startDate.value } satisfies ColumnMeta,
				cell: ({ row }) => <StartDateCell startDate={row.original.startDate} />,
			},
			{
				...actionsColumnBase,
				header: c.actions.value,
				meta: { ...actionsColumnBase.meta, headerLabel: c.actions.value },
				cell: ({ row, table }) => (
					<ActionsCell
						memberId={row.original.id}
						memberEmail={row.original.email}
						onArchive={(table.options.meta as { archiveMember?: (id: string) => void } | undefined)?.archiveMember}
					/>
				),
			},
		],
		[c, departments],
	)
}

export { useMemberColumns }
export type { ColumnMeta }
